<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Listings', path: {name: 'admin-listings'} }]" :name="listingName" no-title/>
    <b-row v-if="!loading">
      <b-col lg="4" md="5">
        <admin-panel with-footer>
          <div class="text-center">
            <b-carousel
              v-if="listing.images"
              controls
              fade
            >
              <b-carousel-slide
                v-for="(image, index) in listing.images"
                :key="index"
              >
                <template #img>
                  <div class="admin-listing-slide">
                    <b-img thumbnail center fluid-grow class="admin-listing-slide-img" :src="image.url"></b-img>
                  </div>
                </template>
              </b-carousel-slide>
            </b-carousel>
            <h4 class="card-title mt-3">
              <b-icon-star-fill v-if="listing.is_promoted" title="Featured Listing"/>
              {{ listingName }}
            </h4>
            <h5 v-if="listing.company" class="card-subtitle">{{ listing.inventory_value + ' ' + listing.inventory_unit }}</h5>
            <h6 v-if="listing.company" class="card-subtitle">{{ listing.company.name }}</h6>
            <admin-listing-status-badge :listing="listing"/>
          </div>
          <template v-slot:footer>
            <div class="text-center">
              <b-button v-if="!listing.bids_count && !listing.is_finished" variant="danger" @click.prevent="cancelListing">Cancel Listing</b-button>
              <div>
                Seller: <b-link :to="{name: 'admin-company', params: {id: listing.company.id}}">{{ listing.company.name }}</b-link>
              </div>
              <template v-if="listing.win">
                Buyer: <b-link :to="{name: 'admin-company', params: {id: listing.win.company.id}}">{{ listing.win.company.name }}</b-link>
                <template v-if="listing.win.shipper">
                  <br>Shipper: <b-link :to="{name: 'admin-shipper', params: {id: listing.win.shipper.id}}">{{ listing.win.shipper.name }}</b-link>
                </template>
                <div>
                  <br>Order ID: {{ listing.win.id }}
                </div>
                <div>
                  <br>Order Price: {{ winPrice }}
                </div>
                <template v-if="listing.win.is_buy_now">
                  <br>Won with "buy now"
                </template>
                <template v-if="listing.is_from_buyer_request">
                  <br>Made from buyer request
                </template>

                <div v-if="listing.seller_fee">
                  <br>
                  <strong>Seller Fees:</strong>
                  <admin-listing-fee-info :fee="listing.seller_fee"/>
                </div>

                <div v-if="listing.buyer_fee">
                  <br>
                  <strong>Buyer Fees:</strong>
                  <admin-listing-fee-info :fee="listing.buyer_fee"/>
                </div>
              </template>
            </div>
          </template>
        </admin-panel>
      </b-col>
      <b-col lg="8" md="7">
        <admin-panel sub-title="Listing Information" :second-body="!!listing.win">
          <p v-if="listing.id">
            <strong>Lot # {{ listing.id }}</strong>
          </p>
          <p v-if="listing.is_promoted">
            <strong>This is a Featured Listing</strong>
          </p>
          <div v-if="listing.description" v-html="listing.description"></div>
          <b-table
              stacked
              bordered
              table-class="mb-0 mt-3"
              :items="tableItems"
          />
          <template v-if="!!listing.win" v-slot:second-body>
            <strong>Reviews</strong>
            <b-form-group class="my-3">
              <b-form-checkbox-group
                  v-model="reviews_opened"
                  @change="toggleOpenReviews"
                  :options="[{ text: 'Reviews Opened', value: true }]"
                  size="lg"
                  switches
              ></b-form-checkbox-group>
            </b-form-group>
            <div class="mt-3">
              <b-tabs
                  content-class="p-3 bg-light"
                  nav-class="font-weight-bold text-uppercase text-danger"
                  fill
              >
                <b-tab title="Seller Review">
                  <template v-if="listing.win.seller_review">
                    <div class="mb-2">
                      <strong>Rating</strong>
                    </div>
                    <div class="mb-3">
                      <star-rating :rating="listing.win.seller_review.rating"/>
                    </div>
                    <div class="mb-2">
                      <strong>Comment</strong>
                    </div>
                    <div>{{ listing.win.seller_review.comment }}</div>
                  </template>
                  <template v-else>
                    <strong>Seller did not post review</strong>
                  </template>
                </b-tab>
                <b-tab title="Buyer Review">
                  <template v-if="listing.win.buyer_review">
                    <div class="mb-2">
                      <strong>Rating</strong>
                    </div>
                    <div class="mb-3">
                      <star-rating :rating="listing.win.buyer_review.rating"/>
                    </div>
                    <div class="mb-2">
                      <strong>Comment</strong>
                    </div>
                    <div>{{ listing.win.buyer_review.comment }}</div>
                  </template>
                  <template v-else>
                    <strong>Buyer did not post review</strong>
                  </template>
                </b-tab>
              </b-tabs>
            </div>
          </template>
        </admin-panel>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import {API_ROUTES} from "../api";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import {convertFromUtc, getFormattedPrice} from "../helpers";
import AdminListingStatusBadge from "../components/AdminListingStatusBadge";
import AdminListingFeeInfo from "../components/AdminListingFeeInfo";
import StarRating from "../components/StarRating";

export default {
  name: "AdminListing",
  components: {StarRating, AdminListingFeeInfo, AdminListingStatusBadge, AdminPanel, AdminBreadcrumb, AdminPreloader},
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      listing: {},
      loading: true,
      reviews_opened: []
    }
  },
  computed: {
    listingName() {
      if (!this.listing.id) {
        return "";
      }
      return this.listing.title;
    },
    winPrice() {
      if (!this.listing.win) {
        return "";
      }
      return getFormattedPrice(this.listing.win.win_price);
    },
    startsAt() {
      if (!this.listing.starts_at) {
        return "";
      }
      return convertFromUtc(this.listing.starts_at, "MM/DD/YYYY HH:mm A");
    },
    endsAt() {
      if (!this.listing.expires_at) {
        return "";
      }
      return convertFromUtc(this.listing.expires_at, "MM/DD/YYYY HH:mm A");
    },
    paidAt() {
      if (!this.listing.is_paid) {
        return "";
      }
      return convertFromUtc(this.listing.win.payment.paid_at, "MM/DD/YYYY HH:mm A");
    },
    producedOn() {
      if (!this.listing.produced_at) {
        return null;
      }
      return convertFromUtc(this.listing.produced_at, "MM/DD/YYYY");
    },
    cannabinoids() {
      if (!this.listing.cannabinoids || !this.listing.cannabinoids.length) {
        return "n/a";
      }
      return this.listing.cannabinoids.map(c => {
        let value = c.value_max ? `${c.value}-${c.value_max}` : c.value;
        return `${c.type}: ${value}${c.unit}`;
      }).join('; ');
    },
    tableItems() {
      if (!this.listing.id) {
        return [];
      }
      return [{
        cannabinoids: this.cannabinoids,
        category_type: this.listing.category_type.name,
        category: this.listing.category.name,
        strain_type: this.listing.strain_type.name,
        strains: this.listing.strains.join(', '),
        production_type: this.listing.production_type.name,
        production_date: this.producedOn ?? 'n/a',
        days_duration: this.listing.days_duration,
        start_date: this.startsAt,
        end_date: this.endsAt,
        start_price: getFormattedPrice(this.listing.start_price),
        reserve_price: this.listing.reserve_price ? getFormattedPrice(this.listing.reserve_price) : "n/a",
        buy_now_price: this.listing.buy_now_price ? getFormattedPrice(this.listing.buy_now_price) : "n/a",
        current_bid: `${getFormattedPrice(this.listing.current_bid)} (Total bids: ${this.listing.bids_count})`
      }];
    }
  },
  created() {
    this.$api.get(`${API_ROUTES.ADMIN_LISTINGS}/${this.id}`).then(({data}) => {
      this.listing = data.listing;
    }).catch(({response: {data}}) => {
      this.$bvToast.toast(data.message ?? 'Invalid Request', {
        variant: 'danger',
        title: 'Error!'
      });
      this.$router.replace({name: "admin-listings"});
    }).finally(() => this.loading = false);
  },
  methods: {
    cancelListing() {
      this.$bvModal.msgBoxConfirm("You are going to cancel this listing", {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_LISTINGS}/${this.id}/cancel`).then(({data}) => {
            this.listing = data.listing;
            this.$bvToast.toast('User has been successfully cancelled', {
              title: 'Success!',
              variant: 'success'
            });
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              variant: 'danger',
              title: 'Error!'
            });
          }).finally(() => this.loading = false);
        }
      });
    },
    toggleOpenReviews() {
      this.loading = true;
      this.$api.post(`${API_ROUTES.ADMIN_LISTINGS}/${this.id}/reviews-status`, {
        opened: this.reviews_opened.includes(true)
      }).then(({data}) => {
        this.listing = data.listing;
        let action = data.listing.win.ready_for_review ? 'opened' : 'closed';
        this.$bvToast.toast(`Reviews have been successfully ${action}`, {
          title: 'Success!',
          variant: 'success'
        });
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to process operation', {
          variant: 'danger',
          title: 'Error!'
        });
      }).finally(() => this.loading = false);
    }
  },
  watch: {
    listing(listing) {
      this.reviews_opened = listing?.win?.ready_for_review ? [true] : [];
    }
  }
}
</script>
